import { useState } from "react";
import { useReflectionStore } from "../../../hooks/useReflectionStore";

interface Props {
  classId: number;
  classTitle: string;
  avgScore: number;
  onDone: () => void;
}

const QUESTIONS = [
  {
    key: "q1",
    ace: "A",
    aceLabel: "Analyze",
    aceColor: "#FF8C00",
    aceBg: "#FFF3E0",
    icon: "🔍",
    label: "AI 결과물에서 이상하거나 부족한 점이 있었나요?",
    placeholder: "환각 현상, 어색한 표현, 예상과 달랐던 부분 등을 솔직하게 적어보세요...",
    tip: "비판적으로 바라보는 것이 AI 활용 역량의 핵심이에요",
  },
  {
    key: "q2",
    ace: "C",
    aceLabel: "Create",
    aceColor: "#00D873",
    aceBg: "#E8F5E9",
    icon: "⚡",
    label: "직접 만들면서 막혔던 순간과 극복 방법은?",
    placeholder: "어떤 부분에서 막혔고, 어떻게 해결했는지 적어보세요...",
    tip: "막힌 순간을 기록하면 다음엔 훨씬 빠르게 넘어갈 수 있어요",
  },
  {
    key: "q3",
    ace: "E",
    aceLabel: "Evaluate",
    aceColor: "#7C3AED",
    aceBg: "#EDE7F6",
    icon: "✏️",
    label: "AI 초안을 내 것으로 만들기 위해 어떻게 다듬었나요?",
    placeholder: "AI 결과물에서 무엇을 수정하고 추가했는지, 다음에 개선할 점은 무엇인지 적어보세요...",
    tip: "Human-in-the-loop — 인간의 판단이 AI를 완성시켜요",
  },
];

/* ─── ACE Summary (final screen) ─── */
function ACESummaryScreen({
  answers,
  classTitle,
  onSave,
  saving,
}: {
  answers: { q1: string; q2: string; q3: string };
  classTitle: string;
  onSave: () => void;
  saving: boolean;
}) {
  const filledCount = [answers.q1, answers.q2, answers.q3].filter((a) => a.trim().length > 0).length;

  return (
    <div className="px-7 pb-7">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="text-4xl mb-2">🎯</div>
        <h4 className="text-lg font-extrabold text-[#191F28] mb-1">ACE 사이클 완료!</h4>
        <p className="text-xs text-[#8B95A1]">
          {filledCount === 3
            ? "세 단계를 모두 기록했어요. 완벽한 성찰이에요!"
            : filledCount > 0
            ? `${filledCount}단계를 기록했어요. 저장하면 노트로 남아요.`
            : "답변 없이도 저장할 수 있어요."}
        </p>
      </div>

      {/* ACE Answer Summary */}
      <div className="space-y-3 mb-6">
        {QUESTIONS.map((q) => {
          const ans = answers[q.key as keyof typeof answers];
          return (
            <div
              key={q.key}
              className="rounded-xl p-3 flex gap-3"
              style={{ backgroundColor: q.aceBg }}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-extrabold text-white"
                style={{ backgroundColor: q.aceColor }}
              >
                {q.ace}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold mb-0.5" style={{ color: q.aceColor }}>
                  {q.aceLabel}
                </p>
                {ans.trim() ? (
                  <p className="text-xs text-[#3D4756] leading-relaxed line-clamp-2">{ans}</p>
                ) : (
                  <p className="text-xs text-[#C4C9D4] italic">작성하지 않았어요</p>
                )}
              </div>
              {ans.trim() && (
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xs" style={{ color: q.aceColor }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ACE trio visual */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {QUESTIONS.map((q, i) => (
          <>
            <div
              key={q.key}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${
                answers[q.key as keyof typeof answers].trim() ? "opacity-100" : "opacity-30"
              }`}
              style={{ backgroundColor: q.aceBg }}
            >
              <span className="text-base">{q.icon}</span>
              <span className="text-[10px] font-extrabold" style={{ color: q.aceColor }}>
                {q.ace}
              </span>
            </div>
            {i < 2 && (
              <i
                key={`arrow-${i}`}
                className="ri-arrow-right-line text-[#C4C9D4] text-sm"
              />
            )}
          </>
        ))}
      </div>

      <button
        onClick={onSave}
        disabled={saving}
        className="w-full py-4 rounded-2xl text-sm font-extrabold text-white bg-[#191F28] cursor-pointer hover:opacity-90 transition-all whitespace-nowrap flex items-center justify-center gap-2"
      >
        {saving ? (
          <><i className="ri-loader-4-line animate-spin" />저장 중...</>
        ) : (
          <>💾 ACE 일지 저장하기</>
        )}
      </button>
    </div>
  );
}

export default function ReflectionModal({ classId, classTitle, avgScore, onDone }: Props) {
  const { addReflection } = useReflectionStore();
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "" });
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const currentQ = step < QUESTIONS.length ? QUESTIONS[step] : null;
  const currentAnswer = currentQ ? answers[currentQ.key as keyof typeof answers] : "";
  const isLast = step === QUESTIONS.length - 1;
  const isSummary = step === QUESTIONS.length;

  const handleNext = () => {
    if (isLast) {
      setStep(QUESTIONS.length); // go to summary
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleSave = () => {
    setSaving(true);
    addReflection({ classId, classTitle, avgScore, q1: answers.q1, q2: answers.q2, q3: answers.q3 });
    setTimeout(onDone, 400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-[#191F28] p-7 relative overflow-hidden flex-shrink-0">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#00D873]/10 rounded-full translate-x-10 -translate-y-10 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold bg-[#00D873]/20 text-[#00D873] px-2.5 py-1 rounded-full">
                📓 ACE 성찰 일지
              </span>
              <span className="text-xs text-white/40">
                {isSummary ? "완료 요약" : `${step + 1} / ${QUESTIONS.length}`}
              </span>
            </div>
            <h3 className="text-lg font-black text-white">{classTitle}</h3>
            <p className="text-white/50 text-xs mt-1">Analyze → Create → Evaluate 사이클로 성장해요</p>
          </div>
        </div>

        {/* ACE Progress */}
        <div className="flex gap-0 px-7 pt-5 pb-2 flex-shrink-0">
          {QUESTIONS.map((q, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full h-1.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: i < step || isSummary ? q.aceColor : i === step ? q.aceColor + "60" : "#F2F4F6",
                }}
              />
              <span
                className="text-[10px] font-bold transition-colors duration-300"
                style={{ color: i < step || isSummary ? q.aceColor : i === step ? q.aceColor : "#C8CDD4" }}
              >
                {q.ace}
              </span>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {isSummary ? (
            <ACESummaryScreen
              answers={answers}
              classTitle={classTitle}
              onSave={handleSave}
              saving={saving}
            />
          ) : (
            <div className="px-7 pb-6">
              {/* ACE Badge */}
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold mb-3 mt-2"
                style={{ backgroundColor: currentQ!.aceBg, color: currentQ!.aceColor }}
              >
                <span>{currentQ!.ace}</span>
                <span>—</span>
                <span>{currentQ!.aceLabel}</span>
              </div>

              <div className="flex items-start gap-2 mb-3">
                <span className="text-xl mt-0.5">{currentQ!.icon}</span>
                <p className="text-base font-extrabold text-[#191F28] leading-snug">{currentQ!.label}</p>
              </div>

              {/* Tip */}
              <div className="flex items-center gap-1.5 mb-4">
                <i className="ri-lightbulb-line text-[#F9A825] text-xs" />
                <span className="text-[11px] text-[#8B95A1] font-medium">{currentQ!.tip}</span>
              </div>

              <textarea
                key={currentQ!.key}
                value={currentAnswer}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [currentQ!.key]: e.target.value }))}
                placeholder={currentQ!.placeholder}
                rows={4}
                maxLength={300}
                className="w-full border-2 border-[#F2F4F6] focus:border-[#00D873] rounded-2xl p-4 text-sm text-[#191F28] resize-none transition-colors outline-none placeholder:text-[#C8CDD4]"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-[#C8CDD4]">{currentAnswer.length}/300</span>
                {currentAnswer.length === 0 && (
                  <span className="text-[11px] text-[#8B95A1]">비워두고 넘어가도 괜찮아요</span>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3 mt-5">
                {step > 0 && (
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    className="w-12 h-12 rounded-xl bg-[#F2F4F6] flex items-center justify-center cursor-pointer hover:bg-[#E8E9EB] transition-colors flex-shrink-0"
                  >
                    <i className="ri-arrow-left-line text-[#8B95A1]" />
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="flex-1 py-3.5 rounded-2xl text-sm font-extrabold text-white cursor-pointer hover:opacity-90 transition-all whitespace-nowrap flex items-center justify-center gap-2"
                  style={{ backgroundColor: currentQ!.aceColor }}
                >
                  {isLast ? (
                    <>결과 보기 <i className="ri-eye-line" /></>
                  ) : (
                    <>다음 단계 <i className="ri-arrow-right-line" /></>
                  )}
                </button>
              </div>

              <button
                onClick={onDone}
                className="w-full mt-3 text-xs text-[#8B95A1] hover:text-[#191F28] transition-colors cursor-pointer py-1 whitespace-nowrap"
              >
                건너뛰기
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
